/**
 * Landing Footer organism component
 * Footer section with app information, links, and branding
 */

// #region Imports
import { Box, Typography, Container, useTheme, Stack, Divider, IconButton, Link as MUILink } from "@mui/material";
import { useTranslation } from "react-i18next";
import { GitHub, LinkedIn, Twitter } from "@mui/icons-material"; // Icons for social links (currently commented out in JSX)
import LinkAtom from "@/components/atoms/Link/link.atom";
// #endregion

// #region Types
/**
 * Props for LandingFooter organism component
 */
interface LandingFooterOrganismProps {
    /** Optional test ID for testing purposes */
    "data-testid"?: string;
}
// #endregion

// #region Component
/**
 * LandingFooter organism component for the page footer
 * Contains branding, navigation, and legal information
 *
 * @param props - The component props
 * @returns JSX element representing the footer
 */
function LandingFooterOrganism({ ...props }: LandingFooterOrganismProps) {
    const { t } = useTranslation("landing");
    const theme = useTheme();
    const currentYear = new Date().getFullYear();

    return (
        <Box
            component="footer"
            role="contentinfo"
            aria-label="Site footer"
            sx={{
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                py: { xs: 6, md: 8 },
            }}
            {...props}
        >
            <Container maxWidth="lg">
                <Stack spacing={6}>
                    {/* Main Footer Content */}
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: {
                                xs: "1fr",
                                md: "2fr 1fr 1fr",
                            },
                            gap: { xs: 4, md: 6 },
                        }}
                    >
                        {/* Brand Section */}
                        <Box>
                            <Typography
                                variant="h4"
                                component="h2"
                                sx={{
                                    mb: 2,
                                    fontWeight: 700,
                                    color: theme.palette.primary.contrastText,
                                }}
                            >
                                Furniturr
                            </Typography>
                            <Typography
                                variant="body1"
                                sx={{
                                    mb: 3,
                                    color: `${theme.palette.primary.contrastText}90`,
                                    lineHeight: 1.6,
                                    maxWidth: 400,
                                }}
                            >
                                {t("footer.description")}
                            </Typography>

                            {/* Social Links - Hidden */}
                            {false && (
                                <Stack direction="row" spacing={1} role="list" aria-label="Social media links">
                                    <IconButton
                                        component="a"
                                        href="https://github.com"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        role="listitem"
                                        sx={{
                                            color: theme.palette.primary.contrastText,
                                            "&:hover, &:focus": {
                                                backgroundColor: `${theme.palette.primary.contrastText}15`,
                                                outline: "2px solid",
                                                outlineColor: theme.palette.primary.contrastText,
                                                outlineOffset: "2px",
                                            },
                                        }}
                                        aria-label={t("footer.social.github")}
                                    >
                                        <GitHub />
                                    </IconButton>
                                    <IconButton
                                        component="a"
                                        href="https://linkedin.com"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        role="listitem"
                                        sx={{
                                            color: theme.palette.primary.contrastText,
                                            "&:hover, &:focus": {
                                                backgroundColor: `${theme.palette.primary.contrastText}15`,
                                                outline: "2px solid",
                                                outlineColor: theme.palette.primary.contrastText,
                                                outlineOffset: "2px",
                                            },
                                        }}
                                        aria-label={t("footer.social.linkedin")}
                                    >
                                        <LinkedIn />
                                    </IconButton>
                                    <IconButton
                                        component="a"
                                        href="https://twitter.com"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        role="listitem"
                                        sx={{
                                            color: theme.palette.primary.contrastText,
                                            "&:hover, &:focus": {
                                                backgroundColor: `${theme.palette.primary.contrastText}15`,
                                                outline: "2px solid",
                                                outlineColor: theme.palette.primary.contrastText,
                                                outlineOffset: "2px",
                                            },
                                        }}
                                        aria-label={t("footer.social.twitter")}
                                    >
                                        <Twitter />
                                    </IconButton>
                                </Stack>
                            )}
                        </Box>

                        {/* Product Links */}
                        <Box component="nav" aria-labelledby="footer-product-nav">
                            <Typography
                                variant="h6"
                                component="h3"
                                id="footer-product-nav"
                                sx={{
                                    mb: 3,
                                    fontWeight: 600,
                                    color: theme.palette.primary.contrastText,
                                }}
                            >
                                {t("footer.links.product.title")}
                            </Typography>
                            <Stack component="ul" spacing={1.5} role="list" sx={{ listStyle: "none", p: 0, m: 0 }}>
                                <Box component="li" role="listitem">
                                    <MUILink
                                        id="footer-home-link"
                                        href="#hero"
                                        variant="body2"
                                        sx={{
                                            color: `${theme.palette.primary.contrastText}80`,
                                            textDecoration: "none",
                                            "&:hover": {
                                                color: theme.palette.primary.contrastText,
                                                textDecoration: "underline",
                                            },
                                        }}
                                    >
                                        {t("footer.links.product.items.hero")}
                                    </MUILink>
                                </Box>
                                <Box component="li" role="listitem">
                                    <MUILink
                                        id="footer-features-link"
                                        href="#features"
                                        variant="body2"
                                        sx={{
                                            color: `${theme.palette.primary.contrastText}80`,
                                            textDecoration: "none",
                                            "&:hover": {
                                                color: theme.palette.primary.contrastText,
                                                textDecoration: "underline",
                                            },
                                        }}
                                    >
                                        {t("footer.links.product.items.features")}
                                    </MUILink>
                                </Box>
                                <Box component="li" role="listitem">
                                    <MUILink
                                        id="footer-how-it-works-link"
                                        href="#how-it-works"
                                        variant="body2"
                                        sx={{
                                            color: `${theme.palette.primary.contrastText}80`,
                                            textDecoration: "none",
                                            "&:hover": {
                                                color: theme.palette.primary.contrastText,
                                                textDecoration: "underline",
                                            },
                                        }}
                                    >
                                        {t("footer.links.product.items.howItWorks")}
                                    </MUILink>
                                </Box>
                                <Box component="li" role="listitem">
                                    <MUILink
                                        id="footer-benefits-link"
                                        href="#benefits"
                                        variant="body2"
                                        sx={{
                                            color: `${theme.palette.primary.contrastText}80`,
                                            textDecoration: "none",
                                            "&:hover": {
                                                color: theme.palette.primary.contrastText,
                                                textDecoration: "underline",
                                            },
                                        }}
                                    >
                                        {t("footer.links.product.items.benefits")}
                                    </MUILink>
                                </Box>
                                <Box component="li" role="listitem">
                                    <MUILink
                                        id="footer-target-audience-link"
                                        href="#target-audience"
                                        variant="body2"
                                        sx={{
                                            color: `${theme.palette.primary.contrastText}80`,
                                            textDecoration: "none",
                                            "&:hover": {
                                                color: theme.palette.primary.contrastText,
                                                textDecoration: "underline",
                                            },
                                        }}
                                    >
                                        {t("footer.links.product.items.targetAudience")}
                                    </MUILink>
                                </Box>
                                <Box component="li" role="listitem">
                                    <MUILink
                                        id="footer-get-started-link"
                                        href="#get-started"
                                        variant="body2"
                                        sx={{
                                            color: `${theme.palette.primary.contrastText}80`,
                                            textDecoration: "none",
                                            "&:hover": {
                                                color: theme.palette.primary.contrastText,
                                                textDecoration: "underline",
                                            },
                                        }}
                                    >
                                        {t("footer.links.product.items.getStarted")}
                                    </MUILink>
                                </Box>
                                <Box component="li" role="listitem">
                                    <LinkAtom
                                        id="footer-generate"
                                        to="/generate"
                                        variant="body2"
                                        sx={{
                                            color: `${theme.palette.primary.contrastText}80`,
                                            textDecoration: "none",
                                            "&:hover": {
                                                color: theme.palette.primary.contrastText,
                                                textDecoration: "underline",
                                            },
                                        }}
                                    >
                                        {t("footer.links.product.items.startOptimizing")}
                                    </LinkAtom>
                                </Box>
                            </Stack>
                        </Box>

                        {/* Company Links */}
                        <Box component="nav" aria-labelledby="footer-company-nav">
                            <Typography
                                variant="h6"
                                component="h3"
                                id="footer-company-nav"
                                sx={{
                                    mb: 3,
                                    fontWeight: 600,
                                    color: theme.palette.primary.contrastText,
                                }}
                            >
                                {t("footer.links.company.title")}
                            </Typography>
                            <Stack component="ul" spacing={1.5} role="list" sx={{ listStyle: "none", p: 0, m: 0 }}>
                                <Box component="li" role="listitem">
                                    <MUILink
                                        id="footer-contact-link"
                                        href="mailto:contact@yazee.me"
                                        variant="body2"
                                        sx={{
                                            color: `${theme.palette.primary.contrastText}80`,
                                            textDecoration: "none",
                                            "&:hover": {
                                                color: theme.palette.primary.contrastText,
                                                textDecoration: "underline",
                                            },
                                        }}
                                    >
                                        {t("footer.links.company.items.contact")}
                                    </MUILink>
                                </Box>
                                <Box component="li" role="listitem">
                                    <LinkAtom
                                        id="privacy-policy"
                                        to="/privacy-policy"
                                        variant="body2"
                                        sx={{
                                            color: `${theme.palette.primary.contrastText}80`,
                                            textDecoration: "none",
                                            "&:hover": {
                                                color: theme.palette.primary.contrastText,
                                                textDecoration: "underline",
                                            },
                                        }}
                                    >
                                        {t("footer.links.company.items.privacyPolicy")}
                                    </LinkAtom>
                                </Box>
                                <Box component="li" role="listitem">
                                    <LinkAtom
                                        id="terms-of-service"
                                        to="/terms-of-service"
                                        variant="body2"
                                        sx={{
                                            color: `${theme.palette.primary.contrastText}80`,
                                            textDecoration: "none",
                                            "&:hover": {
                                                color: theme.palette.primary.contrastText,
                                                textDecoration: "underline",
                                            },
                                        }}
                                    >
                                        {t("footer.links.company.items.termsOfService")}
                                    </LinkAtom>
                                </Box>
                            </Stack>
                        </Box>
                    </Box>

                    <Divider sx={{ borderColor: `${theme.palette.primary.contrastText}20` }} />

                    {/* Bottom Bar */}
                    <Stack
                        direction={{ xs: "column", md: "row" }}
                        justifyContent="space-between"
                        alignItems="center"
                        spacing={2}
                    >
                        <Typography
                            variant="body2"
                            sx={{
                                color: `${theme.palette.primary.contrastText}70`,
                                textAlign: { xs: "center", md: "left" },
                            }}
                        >
                            {t("footer.copyright", { year: currentYear })}
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                color: `${theme.palette.primary.contrastText}70`,
                                textAlign: { xs: "center", md: "right" },
                            }}
                        >
                            {t("footer.madeWith")}
                        </Typography>
                    </Stack>
                </Stack>
            </Container>
        </Box>
    );
}
// #endregion

// #region Exports
export default LandingFooterOrganism;
export type { LandingFooterOrganismProps };
// #endregion
